import { Component, HostListener, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import {
    doc,
    Firestore,
    setDoc,
    getDoc,
} from '@angular/fire/firestore';
import {
    Storage,
    ref,
    uploadBytes,
    getDownloadURL,
} from '@angular/fire/storage';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { UxserviceService } from 'src/app/shared/services/uxservice.service';
import { GeminiService } from 'src/app/shared/services/gemini.service';

@Component({
    selector: 'app-role-selection-dialog',
    templateUrl: './role-selection-dialog.component.html',
    styleUrls: ['./role-selection-dialog.component.scss'],
})
export class RoleSelectionDialogComponent implements OnInit {
    /** 'choose' | 'customer' | 'vendor' */
    currentView: 'choose' | 'customer' | 'vendor' = 'choose';

    isSubmitting = false;
    selectedFile: File | null = null;
    aiExtractedLicenseNo = '';
    isLicenseMismatch = false;
    licenseMismatchError = '';


    // ── Customer form data ──
    customerData: any = {
        firstname: '',
        isIndividual: false,
        accountdetails: {
            companyname: '',
            tradeLicenseNo: '',
            issuedBy: '',
            expiryDate: '',
            emailid: '',
            phoneNumber: '',
            countryCode: '+971',
            tradeLicenseCopyUrl: '',
            companyAddress: '',
            companyDocuments: [],
        },
    };

    // ── Vendor form data (mirrors register-business step 2) ──
    vendorData: any = {
        firstname: '',
        businessType: '',
        accountdetails: {
            companyname: '',
            tradeLicenseNo: '',
            issuedBy: '',
            expiryDate: '',
            emailid: '',
            phoneNumber: '',
            countryCode: '+971',
            tradeLicenseCopyUrl: '',
            companyAddress: '',
            companyDocuments: [],
        },
    };

    // ── Calendar (for license expiry) ──
    showExpiryCalendar = false;
    expiryDate: Date | null = null;
    expiryDisplay: string | null = null;
    currentDate: Date = new Date();
    currentMonth: number = this.currentDate.getMonth();
    currentYear: number = this.currentDate.getFullYear();
    monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December',
    ];
    weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    calendarDays: any[] = [];

    get currentMonthName() {
        return this.monthNames[this.currentMonth];
    }

    years: number[] = [];

    generateYears() {
        const currentYear = new Date().getFullYear();
        this.years = [];
        for (let i = currentYear - 5; i <= currentYear + 20; i++) {
            this.years.push(i);
        }
    }

    selectMonth(month: number) {
        this.currentMonth = month;
        this.generateCalendar();
    }

    selectYear(year: number) {
        this.currentYear = year;
        this.generateCalendar();
    }

    onExpiryInput(event: any) {
        const value = event.target.value;
        // Format: DD/MM/YYYY
        const parts = value.split('/');
        if (parts.length === 3) {
            const d = parseInt(parts[0], 10);
            const m = parseInt(parts[1], 10) - 1;
            const y = parseInt(parts[2], 10);

            if (!isNaN(d) && !isNaN(m) && !isNaN(y) && y > 1900 && y < 2100) {
                const newDate = new Date(y, m, d);
                if (newDate.getDate() === d && newDate.getMonth() === m && newDate.getFullYear() === y) {
                    this.expiryDate = newDate;
                    this.currentMonth = m;
                    this.currentYear = y;
                    this.generateCalendar();
                }
            }
        }
    }

    // ── Country list ──
    countryList = [
        { name: 'UAE', code: '+971', flag: 'assets/flags/uae.png', limit: 9 },
        { name: 'India', code: '+91', flag: 'assets/flags/india.png', limit: 10 },
        { name: 'Saudi Arabia', code: '+966', flag: 'assets/flags/saudi.png', limit: 9 },
        { name: 'Kuwait', code: '+965', flag: 'assets/flags/kuwait.png', limit: 8 },
        { name: 'Oman', code: '+968', flag: 'assets/flags/oman.png', limit: 8 },
        { name: 'Qatar', code: '+974', flag: 'assets/flags/qatar.png', limit: 8 },
    ];

    get currentPhoneLimit(): number {
        const data = this.currentView === 'vendor' ? this.vendorData : this.customerData;
        const country = this.countryList.find(
            (c) => c.code === data.accountdetails.countryCode,
        );
        return country ? country.limit : 10;
    }

    getSelectedCountry() {
        const data = this.currentView === 'vendor' ? this.vendorData : this.customerData;
        return this.countryList.find(
            (c) => c.code === data.accountdetails.countryCode,
        );
    }

    numberOnly(event: any): boolean {
        const charCode = event.which ? event.which : event.keyCode;
        if (charCode > 31 && (charCode < 48 || charCode > 57)) return false;
        return true;
    }

    constructor(
        private auth: Auth,
        private afs: Firestore,
        private storage: Storage,
        private functions: Functions,
        private router: Router,
        private uxservice: UxserviceService,
        private dialogRef: MatDialogRef<RoleSelectionDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private geminiService: GeminiService,
    ) {
        if (data?.startView) {
            this.currentView = data.startView;
        }
        if (data?.businessType) {
            this.vendorData.businessType = data.businessType;
        }
    }

    ngOnInit(): void {
        this.generateCalendar();
        this.generateYears();
        // Pre-fill vendor firstname from auth display name if available
        const fbUser = this.auth.currentUser;
        if (fbUser) {
            const firstName = fbUser.displayName
                ? fbUser.displayName.split(' ')[0]
                : '';
            this.vendorData.firstname = firstName;
            this.vendorData.accountdetails.emailid = fbUser.email || '';
            this.customerData.firstname = firstName;
            this.customerData.accountdetails.emailid = fbUser.email || '';
        }
    }

    selectRole(role: 'customer' | 'vendor') {
        this.currentView = role;
    }

    async skipSelection() {
        const fbUser = this.auth.currentUser;
        if (!fbUser) {
            this.dialogRef.close('skip');
            return;
        }
        try {
            const userRef = doc(this.afs, 'users-list', fbUser.uid);
            await setDoc(
                userRef,
                {
                    role: 'customer',
                    isIndividual: true,
                    isVisiting: false, // Finalizing as Individual Customer
                    updatedAt: new Date().toISOString(),
                },
                { merge: true },
            );
        } catch (_) {
            // Non-critical – proceed even if write fails
        }
        this.dialogRef.close('skip');
        this.router.navigate(['/']);
    }

    goBack() {
        if (this.data?.startView) {
            // Signal to parent to go back to previous dialog/view
            this.dialogRef.close({ action: 'back' });
        } else {
            this.currentView = 'choose';
        }
    }

    // ── Customer Submit ──
    async submitCustomer(form: NgForm, e: Event) {
        e.preventDefault();
        if (form.invalid) {
            Object.keys(form.controls).forEach((k) => form.controls[k]?.markAsTouched());
            return;
        }

        const fbUser = this.auth.currentUser;
        if (!fbUser) { this.uxservice.showError('Session expired. Please log in again.'); return; }

        this.isSubmitting = true;
        this.uxservice.showOverlay('Saving your details...');

        try {
            const userRef = doc(this.afs, 'users-list', fbUser.uid);
            const phoneNumber = `${this.customerData.accountdetails.countryCode}${this.customerData.accountdetails.phoneNumber}`;
            const tradeLicenseVerified = !this.customerData.isIndividual
                ? await this.updateLicenseVerification(fbUser.uid, this.customerData.accountdetails.tradeLicenseNo)
                : false;

            const dataToSave: any = {
                firstname: this.customerData.firstname,
                displayname: this.customerData.firstname,
                role: 'customer',
                isVisiting: false,
                updatedAt: new Date().toISOString(),
                isIndividual: this.customerData.isIndividual || false
            };

            if (!this.customerData.isIndividual) {
                dataToSave.accountdetails = {
                    companyname: this.customerData.accountdetails.companyname,
                    tradeLicenseNo: this.customerData.accountdetails.tradeLicenseNo,
                    issuedBy: this.customerData.accountdetails.issuedBy,
                    expiryDate: this.customerData.accountdetails.expiryDate,
                    emailid: this.customerData.accountdetails.emailid,
                    phoneNumber,
                    tradeLicenseCopyUrl: this.customerData.accountdetails.tradeLicenseCopyUrl || '',
                    companyAddress: this.customerData.accountdetails.companyAddress || '',
                    tradeLicenseVerified,
                };
                dataToSave.tradeLicenseVerified = tradeLicenseVerified;
                if (this.customerData.accountdetails.companyDocuments?.length > 0) {
                    dataToSave.companyDocuments = this.customerData.accountdetails.companyDocuments;
                }
            } else {
                dataToSave.accountdetails = {
                    phoneNumber,
                    emailid: this.customerData.accountdetails.emailid,
                };
            }

            await setDoc(userRef, dataToSave, { merge: true });
            this.uxservice.instantCloseOverlay();
            this.uxservice.showSuccess('Welcome aboard!');
            this.dialogRef.close('customer');
        } catch (err: any) {
            this.uxservice.instantCloseOverlay();
            this.uxservice.showError(err.message);
            this.isSubmitting = false;
        }
    }

    // ── Vendor Submit ──
    async submitVendor(form: NgForm, e: Event) {
        e.preventDefault();
        if (form.invalid) {
            Object.keys(form.controls).forEach((k) => form.controls[k]?.markAsTouched());
            return;
        }

        const fbUser = this.auth.currentUser;
        if (!fbUser) { this.uxservice.showError('Session expired. Please log in again.'); return; }

        this.isSubmitting = true;
        this.uxservice.showOverlay('Creating your business account...');

        try {
            const userRef = doc(this.afs, 'users-list', fbUser.uid);
            const phoneNumber = `${this.vendorData.accountdetails.countryCode}${this.vendorData.accountdetails.phoneNumber}`;
            const tradeLicenseVerified = await this.updateLicenseVerification(
                fbUser.uid,
                this.vendorData.accountdetails.tradeLicenseNo
            );

            await setDoc(
                userRef,
                {
                    firstname: this.vendorData.firstname,
                    displayname: this.vendorData.firstname,
                    role: 'business',
                    isVisiting: false,
                    updatedAt: new Date().toISOString(),
                    accountdetails: {
                        companyname: this.vendorData.accountdetails.companyname,
                        tradeLicenseNo: this.vendorData.accountdetails.tradeLicenseNo,
                        issuedBy: this.vendorData.accountdetails.issuedBy,
                        expiryDate: this.vendorData.accountdetails.expiryDate,
                        emailid: this.vendorData.accountdetails.emailid,
                        phoneNumber,
                        tradeLicenseCopyUrl: this.vendorData.accountdetails.tradeLicenseCopyUrl || '',
                        businessType: this.vendorData.businessType || '', // Save selected business type
                        companyAddress: this.vendorData.accountdetails.companyAddress || '',
                        tradeLicenseVerified,
                    },
                    tradeLicenseVerified,
                    companyDocuments: this.vendorData.accountdetails.companyDocuments || [],
                },
                { merge: true },
            );

            this.uxservice.instantCloseOverlay();
            this.uxservice.showSuccess('Business account set up successfully!');
            this.dialogRef.close('vendor');
            this.router.navigate(['/business']);
        } catch (err: any) {
            this.uxservice.instantCloseOverlay();
            this.uxservice.showError(err.message);
            this.isSubmitting = false;
        }
    }

    private fileToDataUrl(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e: any) => resolve(e.target.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    private async updateLicenseVerification(uid: string, typedLicenseNumber: string): Promise<boolean> {
        const licenseRef = doc(this.afs, 'tradeliceancedoc', uid);
        const licenseSnapshot = await getDoc(licenseRef);
        const licenseData = licenseSnapshot.exists() ? licenseSnapshot.data() : null;
        const existingStatus = licenseData?.['status'];
        const existingApproved = licenseData?.['tradeLicenseApproved'];
        const statusToSet = existingStatus || 'pending';
        const approvedToSet = existingApproved !== undefined ? existingApproved : null;

        await setDoc(
            licenseRef,
            {
                uid,
                typedLicenseNumber: typedLicenseNumber || '',
                typedExpiryDate: this.vendorData.accountdetails.expiryDate || '',
                companyName: this.vendorData.accountdetails.companyname || '',
                documentUrl: this.vendorData.accountdetails.tradeLicenseCopyUrl || '',
                status: statusToSet,
                tradeLicenseApproved: approvedToSet,
                verification: approvedToSet === true,
                updatedAt: new Date().toISOString()
            },
            { merge: true }
        );
        return approvedToSet === true;
    }

    private normalizeLicenseNumber(value: unknown): string {
        return typeof value === 'string'
            ? value.trim().toUpperCase().replace(/[\s\-\/]/g, '')
            : '';
    }

    private async validateTradeLicenseWithAI(file: File): Promise<{ approved: boolean; reason: string | null }> {
        const dataUrl = await this.fileToDataUrl(file);
        let mimeType = file.type;
        if (!mimeType || mimeType === 'application/octet-stream') {
            const ext = file.name.split('.').pop()?.toLowerCase();
            if (ext === 'pdf') mimeType = 'application/pdf';
            else if (ext === 'png') mimeType = 'image/png';
            else if (ext === 'webp') mimeType = 'image/webp';
            else if (ext === 'jpg' || ext === 'jpeg') mimeType = 'image/jpeg';
            else mimeType = 'application/pdf';
        }

        type ValidateDocRequest = { fileBase64: string; mimeType: string };
        type ValidateDocResponse = { approved: boolean; reason: string | null; checks?: any };

        const validateFn = httpsCallable<ValidateDocRequest, ValidateDocResponse>(
            this.functions,
            'validateTradeLicense'
        );

        const result = await validateFn({
            fileBase64: dataUrl,
            mimeType,
        });
        return result.data;
    }

    // ── File Upload ──
    async onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            const file = input.files[0];
            this.selectedFile = file;

            // Size Restriction: 10MB
            const MAX_SIZE = 10 * 1024 * 1024; // 10MB
            if (file.size > MAX_SIZE) {
                this.uxservice.showError('File size exceeds the 10MB limit.');
                input.value = '';
                return;
            }

            const allowedExts = ['pdf', 'jpg', 'jpeg', 'png', 'webp'];
            const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
            if (!allowedExts.includes(fileExt)) {
                this.uxservice.showError('Please upload your Trade License in PDF, JPG, or PNG format.');
                input.value = '';
                return;
            }

            // Step 1: Scan and verify with Gemini AI
            this.uxservice.showOverlay('Scanning & verifying Trade License with AI...');

            try {
                const validationResult = await this.validateTradeLicenseWithAI(file);
                let isApproved = validationResult?.approved;

                if (!isApproved && validationResult?.reason?.includes('reliably read a license number')) {
                    isApproved = true;
                }

                if (!isApproved) {
                    this.uxservice.instantCloseOverlay();
                    const reason = validationResult?.reason || 'The uploaded file does not appear to be a valid Trade License. Please upload an official trade license document.';
                    this.uxservice.showError(reason);
                    input.value = '';
                    this.selectedFile = null;
                    return;
                }

                // Step 2: Upload upon successful AI verification
                this.uxservice.showOverlay('Trade License verified! Uploading...');

                const filePath = `tradeLicenses/${Date.now()}_${this.selectedFile.name}`;
                const storageRef = ref(this.storage, filePath);
                await uploadBytes(storageRef, this.selectedFile);
                const downloadURL = await getDownloadURL(storageRef);
                const docObj = {
                    name: this.selectedFile.name,
                    url: downloadURL,
                    path: filePath,
                    savedAt: new Date().toISOString()
                };

                if (this.currentView === 'vendor') {
                    this.vendorData.accountdetails.tradeLicenseCopyUrl = downloadURL;
                    this.vendorData.accountdetails.companyDocuments = [docObj];
                } else {
                    this.customerData.accountdetails.tradeLicenseCopyUrl = downloadURL;
                    this.customerData.accountdetails.companyDocuments = [docObj];
                }

                const ext = (validationResult as any)?.extracted;
                if (ext) {
                    if (this.currentView === 'vendor') {
                        if (!this.vendorData.accountdetails.tradeLicenseNo && ext.licenseNumber) {
                            this.vendorData.accountdetails.tradeLicenseNo = ext.licenseNumber;
                        }
                        if (!this.vendorData.accountdetails.expiryDate && ext.expiryDate) {
                            this.vendorData.accountdetails.expiryDate = ext.expiryDate;
                        }
                        if (!this.vendorData.accountdetails.issuedBy && ext.issuingAuthority) {
                            this.vendorData.accountdetails.issuedBy = ext.issuingAuthority;
                        }
                        if (!this.vendorData.accountdetails.companyname && ext.companyNameEnglish) {
                            this.vendorData.accountdetails.companyname = ext.companyNameEnglish;
                        }
                    }
                }

                const fbUser = this.auth.currentUser;
                if (fbUser) {
                    const licenseRef = doc(this.afs, 'tradeliceancedoc', fbUser.uid);
                    await setDoc(
                        licenseRef,
                        {
                            uid: fbUser.uid,
                            documentUrl: downloadURL,
                            fileName: this.selectedFile.name,
                            uploadedAt: new Date().toISOString(),
                            licenseNumber: ext?.licenseNumber || '',
                            expiryDate: ext?.expiryDate || '',
                            status: 'pending',
                            tradeLicenseApproved: null,
                            verification: false
                        },
                        { merge: true }
                    );
                }

                this.uxservice.instantCloseOverlay();
                this.uxservice.showSuccess('Trade license verified and uploaded successfully!');

            } catch (err: any) {
                console.error('AI verification / upload error:', err);
                this.uxservice.instantCloseOverlay();
                const errMsg = err?.message || 'Verification failed. Please ensure your document is clear and try again.';
                this.uxservice.showError(errMsg);
                input.value = '';
                this.selectedFile = null;
            }
        }
    }

    // ── Calendar Logic ──
    @HostListener('document:click')
    closeCalendar() {
        this.showExpiryCalendar = false;
    }

    openExpiryCalendar(event: Event) {
        this.showExpiryCalendar = true;
        this.generateCalendar();
        if (!this.expiryDate) {
            const today = new Date();
            this.expiryDate = today;
            this.expiryDisplay = this.formatDisplayDate(today);
            if (this.currentView === 'vendor') {
                this.vendorData.accountdetails.expiryDate = this.expiryDisplay;
            } else {
                this.customerData.accountdetails.expiryDate = this.expiryDisplay;
            }
        }
        event.stopPropagation();
    }

    formatDisplayDate(date: Date): string {
        const d = String(date.getDate()).padStart(2, '0');
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const y = date.getFullYear();
        return `${d}/${m}/${y}`;
    }

    generateCalendar() {
        const days: any[] = [];
        const firstDay = new Date(this.currentYear, this.currentMonth, 1).getDay();
        const lastDate = new Date(this.currentYear, this.currentMonth + 1, 0).getDate();
        const today = new Date();

        for (let i = 0; i < firstDay; i++) {
            days.push({ date: '', inactive: true, isToday: false, isPast: false, isSelected: false });
        }
        for (let d = 1; d <= lastDate; d++) {
            const isToday =
                this.currentYear === today.getFullYear() &&
                this.currentMonth === today.getMonth() &&
                d === today.getDate();
            const isPast =
                this.currentYear < today.getFullYear() ||
                (this.currentYear === today.getFullYear() && this.currentMonth < today.getMonth()) ||
                (this.currentYear === today.getFullYear() && this.currentMonth === today.getMonth() && d < today.getDate());
            
            const isSelected = this.expiryDate && 
                               this.expiryDate.getDate() === d && 
                               this.expiryDate.getMonth() === this.currentMonth && 
                               this.expiryDate.getFullYear() === this.currentYear;

            days.push({ date: d, inactive: false, isToday, isPast, isSelected });
        }
        this.calendarDays = days;
    }

    prevMonth() {
        this.currentMonth--;
        if (this.currentMonth < 0) { this.currentMonth = 11; this.currentYear--; }
        this.generateCalendar();
    }

    nextMonth() {
        this.currentMonth++;
        if (this.currentMonth > 11) { this.currentMonth = 0; this.currentYear++; }
        this.generateCalendar();
    }

    selectExpiryDay(day: any) {
        if (day.inactive) return;
        this.expiryDate = new Date(this.currentYear, this.currentMonth, day.date);
        this.expiryDisplay = this.formatDisplayDate(this.expiryDate);
        if (this.currentView === 'vendor') {
            this.vendorData.accountdetails.expiryDate = this.expiryDisplay;
        } else {
            this.customerData.accountdetails.expiryDate = this.expiryDisplay;
        }
        this.showExpiryCalendar = false;
    }

    closeDialog() {
        this.dialogRef.close();
    }

    isImage(filename: string): boolean {
        if (!filename) return false;
        const ext = filename.split('.').pop()?.toLowerCase();
        return ext === 'jpg' || ext === 'jpeg' || ext === 'png' || ext === 'webp' || ext === 'gif';
    }

    isDoc(filename: string): boolean {
        if (!filename) return false;
        const ext = filename.split('.').pop()?.toLowerCase();
        return ext === 'doc' || ext === 'docx';
    }
}
